import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import appIcon from '../assets/app-icon.png';
import { studentSiteUrl, teacherSiteUrl } from './urls';

/** The site's own pages, in header order. Schools and About join here later. */
type SitePage = 'hub' | 'teachers' | 'students';

interface SiteHeaderProps {
  /** Which page this is: its link is marked current. */
  current: SitePage;
  /** Which app is rendering the header, so links within it stay in the app. */
  app: 'student' | 'teacher';
  /** Shown after the logo on wide screens, e.g. "for Teachers". */
  brandSub?: string;
  /** The page's own actions — Sign in, Start free. */
  actions: ReactNode;
}

/**
 * One header for the front door's pages — the hub, `/teachers` and `/students` —
 * so the three read as one site across the two apps. The page links hide on a
 * phone, where the hub's two cards and the footers do that job.
 */
export function SiteHeader({ current, app, brandSub, actions }: SiteHeaderProps) {
  const links: { page: SitePage; label: string; app: 'student' | 'teacher'; path: string }[] = [
    { page: 'teachers', label: 'For teachers', app: 'teacher', path: '/teachers' },
    { page: 'students', label: 'For students', app: 'student', path: '/students' },
  ];

  const siteLink = (target: 'student' | 'teacher', path: string, className: string, children: ReactNode, extra?: object) =>
    target === app ? (
      <Link to={path} className={className} {...extra}>{children}</Link>
    ) : (
      <a href={`${target === 'teacher' ? teacherSiteUrl() : studentSiteUrl()}${path}`} className={className} {...extra}>{children}</a>
    );

  return (
    <header className="fd-nav">
      <div className="fd-wrap fd-nav-inner">
        {siteLink('student', '/', 'fd-brand', (
          <>
            <img src={appIcon} alt="" />
            BuzzBrain {brandSub && <span className="fd-brand-sub">{brandSub}</span>}
          </>
        ))}
        <nav className="fd-nav-links" aria-label="Main">
          {links.map((l) =>
            siteLink(
              l.app,
              l.path,
              `fd-nav-link fd-hide-sm${l.page === current ? ' fd-nav-link--current' : ''}`,
              l.label,
              { key: l.page, 'aria-current': l.page === current ? 'page' : undefined },
            ),
          )}
          {actions}
        </nav>
      </div>
    </header>
  );
}
